import React, {
  Children,
  cloneElement,
  createContext,
  createElement,
  isValidElement,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";
import withTooltip from "@/hocs/withTooltip";
import { List as ReactWindowList } from "react-window";

import {
  Calendar,
  EllipsisVertical,
  Minus,
  Search,
  SearchX,
  SortAsc,
  SortDesc,
  X,
} from "lucide-react";

import AvatarBase from "@/components/avatar";
import InvestmentAvatar from "@/components/investmentAvatar";
import { cn } from "@/components/utils";
import DropdownMenu from "@/components/dropdownMenu";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import Tooltip from "@/components/tooltip";
import { default as BadgesRenderer } from "@/components/badges";
import { Input } from "./ui/input";
import { t, formatDate, formatDateRelative } from "@/i18n";
import { Skeleton } from "@/components/ui/skeleton";
const ROW_HEIGHTS = {
  sm: 38,
  md: 52,
};

const ItemListContext = createContext(null);
const ItemListRowContext = createContext(null);

const RenderVirtualizedItemListRow = ({ index, style, rows }) => {
  const row = rows[index];

  if (!isValidElement(row)) {
    return <div style={style}>{row}</div>;
  }

  return cloneElement(row, {
    isFirstRow: index === 0,
    isLastRow: index === rows.length - 1,
    style: {
      ...row.props.style,
      ...style,
    },
  });
};

export const ItemList = ({
  children,
  className,
  columns,
  hideColumns,
  hideBorders,
  columnClassName,
  rightAlignLastColumn,
  sorting,
  onSort,
  zeroStateRenderer,
  onFilter,
  autoFocusFiltering,
  filterFieldRef,
  filterButtonColumnIndex = 0,
  virtualized = true,
  rowHeight: _rowHeight = "md",
  isLoading = false,
}) => {
  if (!columns?.length) {
    throw new Error("ItemList: 'columns' prop is required");
  }

  const rowHeight = ROW_HEIGHTS[_rowHeight];

  if (!rowHeight) {
    throw new Error(`ItemList: Invalid rowHeight value (${_rowHeight})`);
  }

  const [isFiltering, setIsFiltering] = useState(!!autoFocusFiltering);
  const [sortBy, sortDesc] = sorting ?? [undefined, false];
  const rows = Children.toArray(children).filter((child) => child != null);
  const internalFilterFieldRef = useRef(null);
  const onFilterRef = useRef(onFilter);

  onFilterRef.current = onFilter;

  const columnSizing = columns
    ?.reduce((prev, col) => `${prev} ${col.size || "1fr"}`, "")
    .trim();

  const isLoadingState =
    typeof isLoading === "number" ? isLoading > 0 : Boolean(isLoading);

  const loadingRowCount =
    typeof isLoading === "number" ? isLoading : Math.max(rows.length, 3);

  const getSkeletonWidth = (rowIndex, columnIndex) => {
    const seed = (rowIndex * 17 + columnIndex * 31) % 100;
    const minWidth = columnIndex === 0 ? 36 : 28;
    const maxWidth = columnIndex === 0 ? 88 : 76;

    return `${Math.round(minWidth + (seed / 100) * (maxWidth - minWidth))}%`;
  };

  const loadingStateContent = isLoadingState ? (
    <div className="px-md flex h-full flex-col overflow-auto">
      {Array.from({ length: loadingRowCount }).map((_, idx) => (
        <div
          key={`item-list-skeleton-row-${idx}`}
          style={{
            height: rowHeight,
            minHeight: rowHeight,
            gridTemplateColumns: columnSizing,
          }}
          className="grid items-center justify-between gap-4"
        >
          {columns.map((column, colIdx) => (
            <div key={`item-list-skeleton-col-${idx}-${colIdx}`}>
              <div
                className={cn(
                  "inline-flex h-full w-full max-w-[300px]",
                  column.className,
                  {
                    "justify-end":
                      rightAlignLastColumn && colIdx === columns.length - 1,
                  },
                )}
              >
                <Skeleton
                  className="h-7 shrink-0"
                  style={{ width: getSkeletonWidth(idx, colIdx) }}
                />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  ) : null;

  const zeroStateContent =
    !isLoadingState && rows.length === 0 ? zeroStateRenderer?.() : null;
  const isFilteringAvailable = typeof onFilter === "function";
  const rowsWithPosition = rows.map((row, index) => {
    if (!isValidElement(row)) {
      return row;
    }

    return cloneElement(row, {
      isFirstRow: index === 0,
      isLastRow: index === rows.length - 1,
    });
  });

  // Expose filter input methods to parent component via ref
  useImperativeHandle(
    filterFieldRef,
    () => ({
      focus: () => {
        setIsFiltering(true);
        internalFilterFieldRef.current?.focus();
      },
      reset: () => {
        if (internalFilterFieldRef.current) {
          internalFilterFieldRef.current.value = "";
        }

        onFilterRef.current?.({ value: undefined });
      },
      get element() {
        return internalFilterFieldRef.current;
      },
      get value() {
        return internalFilterFieldRef.current?.value;
      },
    }),
    [],
  );

  // Auto scroll functionality
  const reactWindowListRef = useRef(null);
  const selectedIndex = rows.findIndex((row) => row.props.isSelected);

  useEffect(() => {
    if (
      !isLoading &&
      !isFiltering &&
      rows.length > 0 &&
      reactWindowListRef.current
    ) {
      selectedIndex >= 0 &&
        setTimeout(() => {
          reactWindowListRef.current?.scrollToRow?.({
            align: "smart",
            behavior: "smooth",
            index: selectedIndex,
          });
        }, 0);
    }
  }, [
    isFiltering,
    isLoading,
    rows.length,
    reactWindowListRef.current,
    selectedIndex,
  ]);

  return (
    <ItemListContext.Provider
      value={{
        columnSizing,
        columns,
        rightAlignLastColumn,
        hideBorders,
      }}
    >
      <div
        data-component="ItemList"
        className={cn("flex h-full flex-col rounded-xl", className)}
      >
        {!hideColumns && (
          <div
            style={{ gridTemplateColumns: columnSizing }}
            className={cn(
              "frosted bg-background/30 grid h-[50px] min-h-[50px] items-center justify-between gap-4 rounded-t-xl border border-b-0 border-slate-300/80 px-[16px]",
              columnClassName,
            )}
          >
            {columns.map((column, idx) => (
              <ItemListColumn
                className={cn(
                  "flex font-bold",
                  {
                    "justify-end":
                      rightAlignLastColumn && idx === columns.length - 1,
                  },
                  column.className,
                )}
                title={column.tooltip}
                key={column.id}
              >
                {column.isSortable ? (
                  <Button
                    variant="ghost"
                    className="group px-0"
                    onClick={() =>
                      onSort([
                        column.id,
                        sortBy !== column.id ? false : !sortDesc,
                      ])
                    }
                  >
                    <div
                      className={cn("flex items-center gap-1", {
                        "flex-row-reverse": idx === columns.length - 1,
                      })}
                    >
                      {column.label}
                      {createElement(sortDesc ? SortDesc : SortAsc, {
                        size: 16,
                        className: cn("opacity-0 transition-opacity", {
                          "group-hover:opacity-30": sortBy !== column.id,
                          "opacity-80": sortBy === column.id,
                        }),
                      })}
                    </div>
                  </Button>
                ) : (
                  column.label
                )}
                {idx === filterButtonColumnIndex &&
                  isFilteringAvailable &&
                  !isFiltering && (
                    <Button
                      variant="outline-primary"
                      className="animate-wiggle ml-4 self-center rounded-full bg-transparent"
                      size="sm"
                      onClick={() => setIsFiltering(true)}
                    >
                      <Search size={16} />
                      {t("generic.search")}
                    </Button>
                  )}
              </ItemListColumn>
            ))}
          </div>
        )}
        {isFiltering && (
          <div
            className={cn(
              "frosted bg-background/30 border border-t-0 border-b-0 border-slate-300/80 px-[12px] pb-4",
              columnClassName,
            )}
          >
            <div className="flex items-center gap-1">
              <Input
                placeholder={t("generic.search")}
                className="rounded-xl px-2 py-0"
                autoFocus
                ref={internalFilterFieldRef}
                onChange={(e) => onFilter?.({ value: e.target.value })}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onFilter?.({ value: undefined });
                  setIsFiltering(false);
                }}
              >
                <X size={16} />
              </Button>
            </div>
          </div>
        )}

        {(rows.length || zeroStateContent || loadingStateContent) && (
          <div
            className={cn(
              "bg-background flex h-full flex-col overflow-auto rounded-b-xl border border-slate-300/80",
              { "rounded-t-xl": hideColumns, "border-0": hideBorders },
            )}
          >
            {loadingStateContent && loadingStateContent}
            {!loadingStateContent && zeroStateContent && (
              <div className="bg-background flex h-[56px] items-center justify-center gap-2">
                <SearchX className="text-slate-500!" size={16} />{" "}
                {zeroStateContent}
              </div>
            )}
            {!loadingStateContent &&
              rows.length > 0 &&
              (virtualized ? (
                <ReactWindowList
                  listRef={reactWindowListRef}
                  rowCount={rows.length}
                  rowHeight={rowHeight}
                  rowComponent={RenderVirtualizedItemListRow}
                  rowProps={{ rows: rowsWithPosition }}
                  style={{ height: "100%", overflow: "auto" }}
                />
              ) : (
                rowsWithPosition
              ))}
          </div>
        )}
      </div>
    </ItemListContext.Provider>
  );
};

ItemList.propTypes = {
  children: node,
  className: string,
  sorting: array,
  onSort: func,
  isSortable: bool,
  rowHeight: number,
  virtualized: bool,
  columns: arrayOf(
    shape({
      id: string,
      label: node,
      className: string,
    }),
  ),
  zeroStateRenderer: func,
  isLoading: oneOfType([bool, number]),
};

export const ItemListRow = ({
  children,
  isSelected,
  isChecked,
  isFirstRow,
  isLastRow,
  onClick,
  style,
  className,
  title,
  status,
}) => {
  const itemListContext = useContext(ItemListContext);

  const statusColors = {
    warning: "bg-warning",
    error: "bg-destructive",
    success: "bg-success",
  };

  const statusColorClass = statusColors[status];

  return (
    <ItemListRowContext.Provider value={{ isSelected, isChecked }}>
      <Tooltip value={title}>
        <div
          role="row"
          aria-selected={isSelected}
          onClick={onClick}
          style={{
            ...style,
            gridTemplateColumns: itemListContext.columnSizing,
          }}
          className={cn(
            "bg-background relative grid items-center justify-between gap-4 border border-b-0 border-transparent border-y-slate-200 px-[16px] py-[8px] transition-colors",
            {
              "px-[8px]": itemListContext.hideBorders,
              "border-t-0": isFirstRow,
              "!rounded-t-xl": isFirstRow && itemListContext.hideColumns,
              "!rounded-b-xl": isLastRow,
              "hover:from-primary/10 hover:to-background cursor-pointer hover:bg-gradient-to-r hover:shadow-[white_inset_0_0_50px]":
                typeof onClick === "function",
              "from-primary/20 to-background bg-gradient-to-r shadow-[white_inset_0_0_50px]":
                isSelected || isChecked,
              "border-primary border-y-primary border-2 !border-y-2 px-[14px] py-[7px] [&+div]:border-t-0":
                isSelected,
              "!rounded-b-xl !border-b-2": isSelected && isLastRow,
            },
            className,
          )}
        >
          {status && (
            <div
              className={cn(
                "absolute top-[2px] left-[1px] h-[calc(100%-4px)] w-[4px] rounded",
                statusColorClass,
              )}
            />
          )}
          {children}
        </div>
      </Tooltip>
    </ItemListRowContext.Provider>
  );
};

export const ItemListColumnBase = ({
  children,
  className,
  truncate,
  onClick,
}) => {
  const itemListContext = useContext(ItemListContext);

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex w-full justify-start",
        {
          truncate: truncate,
          "last:justify-end": itemListContext.rightAlignLastColumn,
        },
        className,
      )}
    >
      {truncate ? <div className="truncate">{children}</div> : children}
    </div>
  );
};

ItemListColumnBase.displayName = "ItemListColumnBase";

export const ItemListColumn = withTooltip()(ItemListColumnBase);

const Date = ({
  children,
  format,
  className,
  displayRelativeFromNow = false,
  maxRelativeAge = { value: 24, unit: "hours" },
}) => {
  const formattedDate = displayRelativeFromNow
    ? formatDateRelative(children, {
        maxRelativeAge,
        format,
      })
    : formatDate(children, format);
  const tooltipDate = formatDate(children, {
    dateStyle: "full",
    timeStyle: "short",
  });

  return (
    <Tooltip value={tooltipDate}>
      <div
        className={cn("inline-flex gap-2 truncate overflow-hidden", className)}
      >
        {formattedDate && <Calendar size={16} className="text-slate-300" />}
        {formattedDate}
      </div>
    </Tooltip>
  );
};

const Avatar = ({
  id,
  name,
  extra,
  avatarType = "user",
  className,
  investmentId,
}) => {
  return (
    <div className={cn("flex gap-4 overflow-hidden", className)}>
      {investmentId ? (
        <InvestmentAvatar
          investmentId={investmentId}
          investorName={name}
          investorTypeOfOwner={avatarType}
        />
      ) : (
        <AvatarBase id={id} name={name} type={avatarType} />
      )}
      <div className="flex flex-col justify-center overflow-hidden">
        <div className="truncate font-semibold" title={name}>
          {name}
        </div>
        {typeof extra === "string" || isValidElement(extra) ? (
          <div className="text-foreground/60 truncate" title={extra}>
            {extra}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const Actions = ({ options }) => (
  <div
    onClick={(e) => {
      options.length &&
        e.stopPropagation(); /* Prevent click from propagating to row */
    }}
  >
    {options.length ? (
      <DropdownMenu
        trigger={
          <Button variant="ghost" className="cursor-pointer!">
            <EllipsisVertical />
          </Button>
        }
        options={options}
      />
    ) : (
      <div className="pointer-events-none flex h-[37px] w-[46px] items-center justify-center">
        <Minus className="text-slate-200" />
      </div>
    )}
  </div>
);

const Badges = ({ data, maxCount = 1 }) => (
  <div>
    <BadgesRenderer data={data} maxCount={maxCount} />
  </div>
);

const Check = ({ onCheckedChange } = {}) => {
  const context = useContext(ItemListRowContext);

  return (
    <Checkbox checked={context.isChecked} onCheckedChange={onCheckedChange} />
  );
};

ItemListColumn.Date = Date;
ItemListColumn.Avatar = Avatar;
ItemListColumn.Actions = Actions;
ItemListColumn.Checkbox = Check;
ItemListColumn.Badges = Badges;
